import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import {
  companyNameAndNipCrossValidation,
  EmailValidator,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  SelectItem,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { InputText } from 'primeng/inputtext';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import {
  PublicSubscriptionBillingAddressForm,
  PublicSubscriptionBillingAddressFormGroup,
} from '@web/shared/module/shop';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { map, Observable, of, startWith, switchMap, tap } from 'rxjs';
import {
  PhoneNumberWithPrefix,
  PhoneNumberWithPrefixComponent,
  PublicDictionaryCountrySelectItemsProviderService,
} from '@web/shared/module/dictionary';

@Component({
  selector: 'app-public-subscription-billing-address-form',
  imports: [
    CommonModule,
    Button,
    FormControlComponent,
    InputText,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    ReactiveFormsModule,
    PrimengSelectDirective,
    Select,
    SpinnerFilledComponent,
    PhoneNumberWithPrefixComponent,
  ],
  templateUrl: './public-subscription-billing-address-form.component.html',
  styleUrl: './public-subscription-billing-address-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicSubscriptionBillingAddressFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly dictionaryCountryDataSource = inject(PublicDictionaryCountrySelectItemsProviderService);

  /* prettier-ignore */
  public readonly data = input<Nillable<PublicSubscriptionBillingAddressForm>>();
  /* prettier-ignore */
  public readonly submitClickEvent = output<PublicSubscriptionBillingAddressForm>();
  public readonly cancelClickEvent = output<void>();
  public readonly pending = input<boolean>(false);

  protected readonly stateFormControlAvailability$!: Observable<boolean>;
  protected readonly countrySelectItems$!: Observable<SelectItem<Guid>[]>;
  protected readonly form!: PublicSubscriptionBillingAddressFormGroup;
  protected readonly valueOrDefault = valueOrDefault;

  private readonly destroyRef = inject(DestroyRef);
  public constructor() {
    this.form = this.buildForm();
    this.countrySelectItems$ = this.prepareCountrySelectItemsSource();
    this.stateFormControlAvailability$ = this.prepareStateAvailabilitySource();
  }

  public ngOnInit(): void {
    this.fillForm();

    companyNameAndNipCrossValidation({
      companyNameControl: this.form.controls.companyName,
      companyNipControl: this.form.controls.companyNip,
      destroyRef: this.destroyRef,
    });
  }

  public handleSubmitButtonClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  public handleCancelButtonClick(): void {
    this.cancelClickEvent.emit();
  }

  private toData(): PublicSubscriptionBillingAddressForm {
    const data = this.form.getRawValue();

    return new PublicSubscriptionBillingAddressForm({
      city: data.city,
      companyName: data.companyName,
      companyNip: data.companyNip,
      countryId: data.countryId,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      postCode: data.postCode,
      state: data.state,
      streetLine1: data.streetLine1,
      streetLine2: data.streetLine2,
    });
  }

  private buildForm(): PublicSubscriptionBillingAddressFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      city: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicSubscriptionBillingAddressForm.CityMaxLength)]),
      companyName: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicSubscriptionBillingAddressForm.CompanyNameMaxLength)]),
      companyNip: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicSubscriptionBillingAddressForm.CompanyNipMaxLength)]),
      countryId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      email: this.builder.control<Nillable<string>>(null, [Validators.required, EmailValidator.email]),
      firstName: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicSubscriptionBillingAddressForm.FirstNameMaxLength)]),
      lastName: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicSubscriptionBillingAddressForm.LastNameMaxLength)]),
      phone: this.builder.control<Nillable<PhoneNumberWithPrefix>>(null, [Validators.required]),
      postCode: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicSubscriptionBillingAddressForm.PostCodeMaxLength)]),
      state: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicSubscriptionBillingAddressForm.StateMaxLength)]),
      streetLine1: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicSubscriptionBillingAddressForm.StreetMaxLength)]),
      streetLine2: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicSubscriptionBillingAddressForm.StreetMaxLength)]),
    });
  }

  private fillForm(): void {
    const data = this.data();

    this.form.patchValue({
      city: data?.city,
      companyName: data?.companyName,
      companyNip: data?.companyNip,
      countryId: data?.countryId,
      email: data?.email,
      firstName: data?.firstName,
      lastName: data?.lastName,
      phone: data?.phone,
      postCode: data?.postCode,
      state: data?.state,
      streetLine1: data?.streetLine1,
      streetLine2: data?.streetLine2,
    });
  }

  private prepareCountrySelectItemsSource(): Observable<SelectItem<Guid>[]> {
    return this.dictionaryCountryDataSource.fetchSelectItems('id');
  }

  private prepareStateAvailabilitySource(): Observable<boolean> {
    const { countryId, state } = this.form.controls;

    return countryId.valueChanges.pipe(
      startWith(countryId.value),
      switchMap((countryId) =>
        isNil(countryId)
          ? of(null)
          : this.dictionaryCountryDataSource.fetchIsoCodeForId(countryId),
      ),
      map((code) => code !== 'PL' && !isNil(code)),
      tap((isAvailable) => (isAvailable ? state.enable() : state.disable())),
    );
  }
}
